import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  WEEK_DAYS,
  addHoliday,
  addLegalHolidays,
  holidaysQuery,
  officeWeek,
  removeBreachRule,
  removeCalendar,
  removeHoliday,
  removeProfile,
  saveBreachRule,
  saveCalendar,
  saveOutOfSla,
  saveProfile,
  saveProfileLinks,
  saveTeamManager,
  slaHoursShellQuery,
  weekSummary,
  type BreachRule,
  type Named,
  type SlaHoursShell,
  type SlaProfile,
  type TargetType,
  type Week,
  type WorkCalendar,
} from '../../api/settingsEaseedeskSlaHours'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Business hours & SLA profiles. Ecran nou, numai in v4
 * (cerinta CEC L54, L55, L56).
 *
 * Trei bucati, de sus in jos, in ordinea in care se construiesc:
 *
 *  1. CALENDARELE de lucru - zilele, intervalele, fusul orar, sarbatorile. Un
 *     calendar nou vine cu sarbatorile legale RO pe anul curent si urmatorul.
 *  2. PROFILELE SLA - 24/7 sau dupa un calendar - si unde se aplica: contracte,
 *     servicii din catalog, tipuri de tichet. Ordinea de precedenta e scrisa
 *     pe ecran, fiindca altfel nimeni nu stie de ce un tichet a primit alt
 *     profil decat cel al tipului lui.
 *  3. ESCALADAREA LA INCALCAREA SLA - pe tip de tichet, spre un grup.
 *  4. INSTIINTARILE "OUT OF SLA" - comutatorul cronului care le trimite.
 *  5. SEFII DE ECHIPA - cine e "Team manager" pentru fiecare grup, destinatar
 *     de ales in Notifications settings la evenimentele de SLA.
 *
 * Fara niciun profil, SLA-ul se masoara ca pana azi: timp de ceas.
 */

const KEY = ['settings', 'easeedesk', 'sla_hours']

const TARGET_LABEL: Record<TargetType, string> = {
  contract: 'Contract',
  service: 'Service (catalog)',
  ticket_type: 'Ticket type',
}

function failure(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Something went wrong.'
}

function nameOf(list: Named[], id: number, fallback = ''): string {
  return list.find((n) => n.ID === id)?.name ?? fallback
}

export function SlaHoursPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(slaHoursShellQuery())
  const [calendar, setCalendar] = useState<WorkCalendar | 'new' | null>(null)
  const [holidaysOf, setHolidaysOf] = useState<WorkCalendar | null>(null)
  const [profile, setProfile] = useState<SlaProfile | 'new' | null>(null)
  const [linksOf, setLinksOf] = useState<SlaProfile | null>(null)
  const [rule, setRule] = useState<BreachRule | 'new' | null>(null)

  const redraw = () => void queryClient.invalidateQueries({ queryKey: KEY })

  const remove = useMutation({
    mutationFn: (what: { kind: 'calendar' | 'profile' | 'rule'; id: number }) =>
      what.kind === 'calendar' ? removeCalendar(what.id) : what.kind === 'profile' ? removeProfile(what.id) : removeBreachRule(what.id),
    onSuccess: () => {
      toast('Removed', 'Success')
      redraw()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const data = shell.data
  const ask = (kind: 'calendar' | 'profile' | 'rule', id: number, text: string) => {
    if (window.confirm(text)) remove.mutate({ kind, id })
  }

  return (
    <div className="od-sla-hours">
      <h4>
        <strong>Business hours</strong> &amp; SLA profiles
      </h4>
      <p className="text-muted">
        The SLA clock of a ticket runs only inside the working hours of its profile. The profile is picked in this
        order: <strong>{data.precedence.map((t) => TARGET_LABEL[t]).join(' > ')}</strong>. A ticket with no profile,
        or with a 24/7 profile, is measured in wall-clock time, exactly as before.
      </p>

      {!data.ready ? (
        <div className="alert alert-info od-sla-hours-not-ready">
          <i className="fas fa-info-circle" aria-hidden="true"></i> The business hours tables are not installed on this
          database yet. They arrive with the next release; until then every SLA is measured in wall-clock time.
        </div>
      ) : (
        <>
          <section className="od-sla-calendars">
            <button type="button" className="pull-right btn btn-success btn-sm od-new-calendar" onClick={() => setCalendar('new')}>
              <i className="fas fa-plus" aria-hidden="true"></i> New calendar
            </button>
            <h5>
              <strong>Working calendars</strong>
            </h5>
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Hours</th>
                  <th>Time zone</th>
                  <th>Days off</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.calendars.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      No calendar yet. A calendar says when the SLA clock runs: days, hours and days off.
                    </td>
                  </tr>
                ) : (
                  data.calendars.map((c) => (
                    <tr key={c.ID} data-calendar={c.ID}>
                      <td>{c.name}</td>
                      <td>{weekSummary(c.week)}</td>
                      <td>{c.timezone}</td>
                      <td>
                        <button type="button" className="btn btn-link btn-xs od-holidays" onClick={() => setHolidaysOf(c)}>
                          {c.holidays} day{c.holidays === 1 ? '' : 's'}
                        </button>
                      </td>
                      <td className="text-right">
                        <button type="button" className="btn btn-default btn-xs od-edit-calendar" onClick={() => setCalendar(c)}>
                          <i className="fas fa-pen" aria-hidden="true"></i> Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => ask('calendar', c.ID, `Remove the calendar "${c.name}"?`)}
                        >
                          <i className="fas fa-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <section className="od-sla-profiles">
            <button type="button" className="pull-right btn btn-success btn-sm od-new-profile" onClick={() => setProfile('new')}>
              <i className="fas fa-plus" aria-hidden="true"></i> New SLA profile
            </button>
            <h5>
              <strong>SLA profiles</strong>
            </h5>
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Clock</th>
                  <th>Applies to</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.profiles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      No SLA profile yet: every ticket is measured in wall-clock time.
                    </td>
                  </tr>
                ) : (
                  data.profiles.map((p) => (
                    <tr key={p.ID} data-profile={p.ID}>
                      <td>{p.name}</td>
                      <td>
                        {p.mode === 'always'
                          ? '24/7'
                          : `Business hours: ${nameOf(
                              data.calendars.map((c) => ({ ID: c.ID, name: c.name })),
                              p.calendar_id ?? 0,
                              'missing calendar',
                            )}`}
                      </td>
                      <td className="od-profile-links">{appliesTo(p, data)}</td>
                      <td className="text-right">
                        <button type="button" className="btn btn-default btn-xs od-profile-targets" onClick={() => setLinksOf(p)}>
                          <i className="fas fa-link" aria-hidden="true"></i> Applies to
                        </button>{' '}
                        <button type="button" className="btn btn-default btn-xs" onClick={() => setProfile(p)}>
                          <i className="fas fa-pen" aria-hidden="true"></i> Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => ask('profile', p.ID, `Remove the SLA profile "${p.name}"? Its tickets go back to wall-clock time.`)}
                        >
                          <i className="fas fa-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <section className="od-sla-breach">
            <button type="button" className="pull-right btn btn-success btn-sm od-new-rule" onClick={() => setRule('new')}>
              <i className="fas fa-plus" aria-hidden="true"></i> New rule
            </button>
            <h5>
              <strong>Escalate when the SLA is breached</strong>
            </h5>
            <p className="text-muted">
              Besides the auto-escalation after a time in status, a ticket can be escalated to a group the moment its
              resolution SLA is breached (measured with its profile). Each ticket is escalated this way once.
            </p>
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>Ticket type</th>
                  <th>Escalate to group</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.breach_rules.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      No rule: breached tickets are only notified, not escalated.
                    </td>
                  </tr>
                ) : (
                  data.breach_rules.map((r) => (
                    <tr key={r.ID} data-rule={r.ID}>
                      <td>{r.ticket_type_id === 0 ? 'Any' : nameOf(data.options.ticket_types, r.ticket_type_id, `#${r.ticket_type_id}`)}</td>
                      <td>{nameOf(data.options.groups, r.group_id, `#${r.group_id}`)}</td>
                      <td>{r.active ? 'Yes' : 'No'}</td>
                      <td className="text-right">
                        <button type="button" className="btn btn-default btn-xs" onClick={() => setRule(r)}>
                          <i className="fas fa-pen" aria-hidden="true"></i> Edit
                        </button>{' '}
                        <button type="button" className="btn btn-danger btn-xs" onClick={() => ask('rule', r.ID, 'Remove this rule?')}>
                          <i className="fas fa-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <OutOfSlaSection shell={data} onSaved={redraw} />
          <TeamManagersSection shell={data} onSaved={redraw} />
        </>
      )}

      <CalendarModal calendar={calendar} shell={data} onClose={() => setCalendar(null)} onSaved={redraw} />
      <HolidaysModal calendar={holidaysOf} onClose={() => setHolidaysOf(null)} onChanged={redraw} />
      <ProfileModal profile={profile} shell={data} onClose={() => setProfile(null)} onSaved={redraw} />
      <LinksModal profile={linksOf} shell={data} onClose={() => setLinksOf(null)} onSaved={redraw} />
      <RuleModal rule={rule} shell={data} onClose={() => setRule(null)} onSaved={redraw} />
    </div>
  )
}

function appliesTo(p: SlaProfile, data: SlaHoursShell): string {
  const parts: string[] = []
  const lists: Record<TargetType, Named[]> = {
    contract: data.options.contracts,
    service: data.options.services,
    ticket_type: data.options.ticket_types,
  }
  for (const type of data.precedence) {
    const names = (p.links[type] ?? []).map((id) => nameOf(lists[type], id, `#${id}`))
    if (names.length > 0) parts.push(`${TARGET_LABEL[type]}: ${names.join(', ')}`)
  }

  return parts.length > 0 ? parts.join(' · ') : 'Nothing yet'
}

/* -------------------------------------------------------------------------
 * Calendar
 * ----------------------------------------------------------------------- */

export function CalendarModal({
  calendar,
  shell,
  onClose,
  onSaved,
}: {
  calendar: WorkCalendar | 'new' | null
  shell: SlaHoursShell
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState('Europe/Bucharest')
  const [week, setWeek] = useState<Week>(officeWeek())

  useEffect(() => {
    if (calendar === null) return
    if (calendar === 'new') {
      setName('')
      setTimezone('Europe/Bucharest')
      setWeek(officeWeek())
      return
    }
    setName(calendar.name)
    setTimezone(calendar.timezone)
    setWeek(calendar.week)
  }, [calendar])

  const save = useMutation({
    mutationFn: () => saveCalendar(calendar === 'new' || calendar === null ? 0 : calendar.ID, { name, timezone, week }),
    onSuccess: () => {
      toast(calendar === 'new' ? 'Calendar created, with the Romanian public holidays of this year and the next' : 'Calendar saved', 'Success')
      onSaved()
      onClose()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  const setDay = (day: keyof Week, intervals: [string, string][]) => setWeek({ ...week, [day]: intervals })

  return (
    <Modal
      id="slaCalendarModal"
      open={calendar !== null}
      onClose={onClose}
      title={calendar === 'new' ? 'New working calendar' : 'Edit working calendar'}
      large
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-success od-save-calendar" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="slaCalendarName">Name</label>
        <input id="slaCalendarName" className="form-control" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="slaCalendarZone">Time zone</label>
        <select id="slaCalendarZone" className="form-control" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {shell.options.timezones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </div>
      <table className="table table-condensed od-week">
        <tbody>
          {WEEK_DAYS.map(({ key, label }) => (
            <tr key={key} data-day={key}>
              <th style={{ width: 110 }}>{label}</th>
              <td>
                {(week[key] ?? []).length === 0 ? <span className="text-muted">Day off</span> : null}
                {(week[key] ?? []).map(([from, to], i) => (
                  <span key={i} className="od-interval" style={{ display: 'inline-block', marginRight: 12 }}>
                    <input
                      type="text"
                      aria-label={`${label} from`}
                      value={from}
                      size={5}
                      placeholder="HH:MM"
                      onChange={(e) => setDay(key, week[key].map((iv, j) => (j === i ? [e.target.value, iv[1]] : iv)))}
                    />{' '}
                    -{' '}
                    <input
                      type="text"
                      aria-label={`${label} to`}
                      value={to}
                      size={5}
                      placeholder="HH:MM"
                      onChange={(e) => setDay(key, week[key].map((iv, j) => (j === i ? [iv[0], e.target.value] : iv)))}
                    />{' '}
                    <button
                      type="button"
                      className="btn btn-link btn-xs"
                      aria-label={`Remove ${label} interval`}
                      onClick={() => setDay(key, week[key].filter((_, j) => j !== i))}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="btn btn-default btn-xs od-add-interval"
                  onClick={() => setDay(key, [...(week[key] ?? []), ['09:00', '17:00']])}
                >
                  <i className="fas fa-plus" aria-hidden="true"></i> Hours
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="help-block">
        The end of an interval may be 24:00 (end of the day). For 24/7 put 00:00 - 24:00 on every day, or use a 24/7
        profile instead.
      </p>
    </Modal>
  )
}

export function HolidaysModal({ calendar, onClose, onChanged }: { calendar: WorkCalendar | null; onClose(): void; onChanged(): void }) {
  const queryClient = useQueryClient()
  const id = calendar?.ID ?? 0
  const list = useQuery(holidaysQuery(id))
  const [day, setDay] = useState('')
  const [label, setLabel] = useState('')
  const [year, setYear] = useState(new Date().getFullYear() + 1)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'sla_hours', 'holidays', id] })
    onChanged()
  }

  const change = useMutation({
    mutationFn: (what: { kind: 'add' } | { kind: 'legal' } | { kind: 'remove'; holiday: number }) =>
      what.kind === 'add' ? addHoliday(id, day, label) : what.kind === 'legal' ? addLegalHolidays(id, year) : removeHoliday(id, what.holiday),
    onSuccess: () => {
      setDay('')
      setLabel('')
      refresh()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  return (
    <Modal id="slaHolidaysModal" open={calendar !== null} onClose={onClose} title={`Days off - ${calendar?.name ?? ''}`}>
      <div className="form-inline" style={{ marginBottom: 10 }}>
        <input type="date" className="form-control input-sm" aria-label="Day" value={day} onChange={(e) => setDay(e.target.value)} />{' '}
        <input className="form-control input-sm" placeholder="Name" aria-label="Name" value={label} onChange={(e) => setLabel(e.target.value)} />{' '}
        <button type="button" className="btn btn-success btn-sm od-add-holiday" disabled={day === ''} onClick={() => change.mutate({ kind: 'add' })}>
          Add day
        </button>
      </div>
      <div className="form-inline" style={{ marginBottom: 10 }}>
        <input
          type="number"
          className="form-control input-sm"
          aria-label="Year"
          style={{ width: 90 }}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        />{' '}
        <button type="button" className="btn btn-default btn-sm od-legal-holidays" onClick={() => change.mutate({ kind: 'legal' })}>
          Add the Romanian public holidays of this year
        </button>
      </div>
      {list.isLoading ? <Spinner /> : null}
      <table className="table table-condensed">
        <tbody>
          {(list.data?.holidays ?? []).map((h) => (
            <tr key={h.ID} data-day={h.day}>
              <td style={{ width: 110 }}>{h.day}</td>
              <td>{h.name}</td>
              <td className="text-right">
                <button
                  type="button"
                  className="btn btn-link btn-xs"
                  aria-label={`Remove ${h.day}`}
                  onClick={() => change.mutate({ kind: 'remove', holiday: h.ID })}
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Profil
 * ----------------------------------------------------------------------- */

export function ProfileModal({
  profile,
  shell,
  onClose,
  onSaved,
}: {
  profile: SlaProfile | 'new' | null
  shell: SlaHoursShell
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [mode, setMode] = useState<'always' | 'calendar'>('calendar')
  const [calendarId, setCalendarId] = useState(0)

  useEffect(() => {
    if (profile === null) return
    if (profile === 'new') {
      setName('')
      setMode('calendar')
      setCalendarId(shell.calendars[0]?.ID ?? 0)
      return
    }
    setName(profile.name)
    setMode(profile.mode)
    setCalendarId(profile.calendar_id ?? shell.calendars[0]?.ID ?? 0)
  }, [profile, shell.calendars])

  const save = useMutation({
    mutationFn: () =>
      saveProfile(profile === 'new' || profile === null ? 0 : profile.ID, {
        name,
        mode,
        calendar_id: mode === 'calendar' ? calendarId : null,
      }),
    onSuccess: () => {
      toast('SLA profile saved', 'Success')
      onSaved()
      onClose()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  return (
    <Modal
      id="slaProfileModal"
      open={profile !== null}
      onClose={onClose}
      title={profile === 'new' ? 'New SLA profile' : 'Edit SLA profile'}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-success od-save-profile" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="slaProfileName">Name</label>
        <input id="slaProfileName" className="form-control" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="radio">
        <label>
          <input type="radio" name="slaProfileMode" checked={mode === 'always'} onChange={() => setMode('always')} /> 24/7 - the clock
          always runs (as today)
        </label>
      </div>
      <div className="radio">
        <label>
          <input type="radio" name="slaProfileMode" checked={mode === 'calendar'} onChange={() => setMode('calendar')} /> Business hours
          - the clock runs only inside a calendar
        </label>
      </div>
      {mode === 'calendar' ? (
        <div className="form-group">
          <label htmlFor="slaProfileCalendar">Calendar</label>
          <select
            id="slaProfileCalendar"
            className="form-control"
            value={calendarId}
            onChange={(e) => setCalendarId(Number(e.target.value))}
          >
            {shell.calendars.length === 0 ? <option value={0}>Create a calendar first</option> : null}
            {shell.calendars.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.name} ({weekSummary(c.week)})
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </Modal>
  )
}

export function LinksModal({
  profile,
  shell,
  onClose,
  onSaved,
}: {
  profile: SlaProfile | null
  shell: SlaHoursShell
  onClose(): void
  onSaved(): void
}) {
  const [links, setLinks] = useState<Record<TargetType, number[]>>({ contract: [], service: [], ticket_type: [] })

  useEffect(() => {
    if (profile !== null) setLinks(profile.links)
  }, [profile])

  /* Cine are deja alt profil pe o tinta: legarea il muta aici, si asta se spune. */
  const owner = (type: TargetType, id: number): string => {
    const other = shell.profiles.find((p) => p.ID !== profile?.ID && (p.links[type] ?? []).includes(id))
    return other ? ` (now: ${other.name})` : ''
  }

  const save = useMutation({
    mutationFn: () => saveProfileLinks(profile?.ID ?? 0, links),
    onSuccess: () => {
      toast('Saved', 'Success')
      onSaved()
      onClose()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  const lists: Record<TargetType, Named[]> = {
    contract: shell.options.contracts,
    service: shell.options.services,
    ticket_type: shell.options.ticket_types,
  }

  const toggle = (type: TargetType, id: number, on: boolean) =>
    setLinks({ ...links, [type]: on ? [...links[type], id] : links[type].filter((x) => x !== id) })

  return (
    <Modal
      id="slaLinksModal"
      open={profile !== null}
      onClose={onClose}
      title={`Where "${profile?.name ?? ''}" applies`}
      large
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-success od-save-links" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <p className="text-muted">
        When a ticket matches more than one, the first in this order wins:{' '}
        {shell.precedence.map((t) => TARGET_LABEL[t]).join(' > ')}.
      </p>
      <div className="row">
        {shell.precedence.map((type) => (
          <div key={type} className="col-sm-4" data-target-type={type}>
            <label>{TARGET_LABEL[type]}</label>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {lists[type].length === 0 ? <div className="text-muted">None</div> : null}
              {lists[type].map((item) => (
                <div key={item.ID} className="checkbox" style={{ margin: '2px 0' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={links[type].includes(item.ID)}
                      onChange={(e) => toggle(type, item.ID, e.target.checked)}
                    />{' '}
                    {item.name}
                    <span className="text-muted">{owner(type, item.ID)}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Escaladarea la incalcare
 * ----------------------------------------------------------------------- */

export function RuleModal({
  rule,
  shell,
  onClose,
  onSaved,
}: {
  rule: BreachRule | 'new' | null
  shell: SlaHoursShell
  onClose(): void
  onSaved(): void
}) {
  const [type, setType] = useState(0)
  const [group, setGroup] = useState(0)
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (rule === null) return
    if (rule === 'new') {
      setType(0)
      setGroup(shell.options.groups[0]?.ID ?? 0)
      setActive(true)
      return
    }
    setType(rule.ticket_type_id)
    setGroup(rule.group_id)
    setActive(rule.active)
  }, [rule, shell.options.groups])

  const save = useMutation({
    mutationFn: () => saveBreachRule(rule === 'new' || rule === null ? 0 : rule.ID, { ticket_type_id: type, group_id: group, active }),
    onSuccess: (answer) => {
      if (answer.cron_message) toast(`Rule saved, but the cron was not updated. ${answer.cron_message}`, 'Warning', 'warning')
      else toast('Rule saved', 'Success')
      onSaved()
      onClose()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  return (
    <Modal
      id="slaRuleModal"
      open={rule !== null}
      onClose={onClose}
      title={rule === 'new' ? 'New SLA breach escalation' : 'Edit SLA breach escalation'}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-success od-save-rule" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="slaRuleType">Ticket type</label>
        <select id="slaRuleType" className="form-control" value={type} onChange={(e) => setType(Number(e.target.value))}>
          <option value={0}>Any</option>
          {shell.options.ticket_types.map((t) => (
            <option key={t.ID} value={t.ID}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="slaRuleGroup">Escalate to group</label>
        <select id="slaRuleGroup" className="form-control" value={group} onChange={(e) => setGroup(Number(e.target.value))}>
          {shell.options.groups.map((g) => (
            <option key={g.ID} value={g.ID}>
              {g.name}
            </option>
          ))}
        </select>
      </div>
      <div className="checkbox">
        <label>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
        </label>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Instiintarile "out of SLA" si sefii de echipa
 * ----------------------------------------------------------------------- */

export function OutOfSlaSection({ shell, onSaved }: { shell: SlaHoursShell; onSaved(): void }) {
  const save = useMutation({
    mutationFn: (active: boolean) => saveOutOfSla(active),
    onSuccess: (answer, active) => {
      const what = active ? 'Out of SLA notifications turned on' : 'Out of SLA notifications turned off'
      if (answer.cron_message) toast(`${what}, but the cron was not updated. ${answer.cron_message}`, 'Warning', 'warning')
      else toast(what, 'Success')
      onSaved()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  return (
    <section className="od-sla-out-of-sla">
      <h5>
        <strong>Out of SLA notifications</strong>
      </h5>
      <p className="text-muted">
        Once a ticket exceeds its resolution SLA, the &quot;out of SLA&quot; email is sent once, to the recipients set
        for that event in Notifications settings (assignee, group, team manager...). The check runs every 10 minutes
        while this is on.
      </p>
      {!shell.out_of_sla.ready ? (
        <div className="alert alert-info od-out-of-sla-not-ready">
          <i className="fas fa-info-circle" aria-hidden="true"></i> This setting arrives with the next release.
        </div>
      ) : (
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              className="od-out-of-sla-toggle"
              checked={shell.out_of_sla.active}
              disabled={save.isPending}
              onChange={(e) => save.mutate(e.target.checked)}
            />{' '}
            Send the &quot;out of SLA&quot; notifications
          </label>
        </div>
      )}
    </section>
  )
}

export function TeamManagersSection({ shell, onSaved }: { shell: SlaHoursShell; onSaved(): void }) {
  const save = useMutation({
    mutationFn: (what: { group: number; manager: number }) => saveTeamManager(what.group, what.manager),
    onSuccess: () => {
      toast('Team manager saved', 'Success')
      onSaved()
    },
    onError: (error) => toast(failure(error), 'Error', 'error'),
  })

  const managerOf = (group: number) => shell.team_managers.groups.find((g) => g.group_id === group)?.manager_id ?? 0

  return (
    <section className="od-sla-team-managers">
      <h5>
        <strong>Team managers</strong>
      </h5>
      <p className="text-muted">
        The team manager of the ticket&apos;s group can receive the SLA emails (&quot;out of SLA&quot; and the escalation
        when the SLA is breached): pick <em>Team manager</em> as recipient in Notifications settings.
      </p>
      {!shell.team_managers.ready ? (
        <div className="alert alert-info od-team-managers-not-ready">
          <i className="fas fa-info-circle" aria-hidden="true"></i> Team managers arrive with the next release.
        </div>
      ) : (
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Group</th>
              <th>Team manager</th>
            </tr>
          </thead>
          <tbody>
            {shell.options.groups.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-muted">
                  No group.
                </td>
              </tr>
            ) : (
              shell.options.groups.map((g) => (
                <tr key={g.ID} data-group={g.ID}>
                  <td>{g.name}</td>
                  <td>
                    <select
                      className="form-control input-sm od-team-manager"
                      aria-label={`Team manager of ${g.name}`}
                      value={managerOf(g.ID)}
                      disabled={save.isPending}
                      onChange={(e) => save.mutate({ group: g.ID, manager: Number(e.target.value) })}
                    >
                      <option value={0}>None</option>
                      {managerOf(g.ID) > 0 && !shell.options.operators.some((o) => o.ID === managerOf(g.ID)) ? (
                        <option value={managerOf(g.ID)}>#{managerOf(g.ID)} (inactive)</option>
                      ) : null}
                      {shell.options.operators.map((o) => (
                        <option key={o.ID} value={o.ID}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </section>
  )
}
